import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Edit2, Trash2, Check, ClipboardList, Search, X as XIcon, Layers } from 'lucide-react';
import { toast } from 'sonner';
import {
  getCosturaAvances,
  createCosturaAvance,
  createCosturaAvancesBulk,
  updateCosturaAvance,
  deleteCosturaAvance,
  getCosturaTrabajadoresInternos,
  getCosturaCortesDisponibles,
  getOperacionesCostura,
  checkDuplicadosCosturaAvances,
} from '../services/api';
import { useEmpresa } from '../context/EmpresaContext';

const fmtMoney = (v) => `S/ ${Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d.length === 10 ? d + 'T00:00:00' : d);
  return dt.toLocaleDateString('es-PE');
};
const today = () => new Date().toISOString().split('T')[0];

const emptyRow = () => ({
  uid: Math.random().toString(36).slice(2, 10),
  registro_id: '',
  operacion_id: '',
  cantidad: '',
  tarifa_aplicada: '',  // override opcional
  notas: '',
  usaTarifaCustom: false,
  searchCorte: '',
  corteListOpen: false,
});

export default function CosturaAvances() {
  const { empresaActual } = useEmpresa();
  const [items, setItems] = useState([]);  // avances ya guardados (lista)
  const [trabajadores, setTrabajadores] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [cortes, setCortes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCortes, setLoadingCortes] = useState(false);

  const [filtros, setFiltros] = useState({
    trabajador_id: '',
    operacion_id: '',
    fecha_desde: '',
    fecha_hasta: '',
    solo_pendientes: false,
    busqueda: '',  // texto libre que matchea modelo, n° corte, costurero, operación
  });
  const [soloInterna, setSoloInterna] = useState(true);

  // ── Form de carga masiva ──────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [bulkTrabajadorId, setBulkTrabajadorId] = useState('');
  const [bulkFecha, setBulkFecha] = useState(today());
  const [filas, setFilas] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  // Avances ya existentes para el trabajador+fecha (para detectar duplicados).
  // Set de strings "operacion_id|registro_id".
  const [existentes, setExistentes] = useState([]);
  const dupKey = (opId, regId) => `${opId}|${regId}`;
  const dupSet = useMemo(
    () => new Set((existentes || []).map(e => dupKey(e.operacion_id, e.registro_id))),
    [existentes]
  );
  // Trigger global de búsqueda — cuando cambia el costurero o el toggle interna
  // todos los buscadores recargan. Si una fila tiene su propio search, ese
  // refresca solo ese.
  const searchDebounceRef = useRef({});

  // ── Edición individual (modal aparte) ────────────────────────────────
  const [editingAvance, setEditingAvance] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const cargarAvances = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.trabajador_id) params.trabajador_id = filtros.trabajador_id;
      if (filtros.operacion_id) params.operacion_id = filtros.operacion_id;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.solo_pendientes) params.solo_pendientes = true;
      const r = await getCosturaAvances(params);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Error al cargar avances');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const cargarMaestros = useCallback(async () => {
    try {
      const [tr, op] = await Promise.all([
        getCosturaTrabajadoresInternos(),
        getOperacionesCostura(),
      ]);
      setTrabajadores(tr.data || []);
      setOperaciones((op.data || []).filter(o => o.activo));
    } catch (e) {
      toast.error('Error al cargar maestros');
    }
  }, []);

  // Cargar cortes — query global compartida por todas las filas. Cuando una
  // fila tiene un search distinto, lo resuelve filtrando localmente sobre la
  // misma data (para evitar 1 fetch por fila).
  const cargarCortes = useCallback(async (search = '') => {
    setLoadingCortes(true);
    try {
      const params = { solo_interna: soloInterna };
      if (search) params.search = search;
      if (bulkTrabajadorId) params.trabajador_id = parseInt(bulkTrabajadorId);
      const r = await getCosturaCortesDisponibles(params);
      setCortes(r.data || []);
    } catch (e) {
      // silencio
    } finally {
      setLoadingCortes(false);
    }
  }, [soloInterna, bulkTrabajadorId]);

  useEffect(() => {
    if (!empresaActual) return;
    cargarMaestros();
    cargarAvances();
  }, [empresaActual, cargarMaestros, cargarAvances]);

  // Recargar cortes al cambiar costurero / toggle interna mientras el form está abierto
  useEffect(() => {
    if (!empresaActual || !showForm) return;
    cargarCortes('');
  }, [empresaActual, showForm, cargarCortes]);

  // Al cambiar trabajador o fecha, traer los avances ya existentes para
  // detectar duplicados proactivamente (warning visual en la fila antes de
  // que el usuario apriete guardar).
  useEffect(() => {
    if (!empresaActual || !showForm || !bulkTrabajadorId || !bulkFecha) {
      setExistentes([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const r = await checkDuplicadosCosturaAvances({
          trabajador_id: parseInt(bulkTrabajadorId), fecha: bulkFecha,
        });
        if (!cancel) setExistentes(r.data || []);
      } catch (_) {
        if (!cancel) setExistentes([]);
      }
    })();
    return () => { cancel = true; };
  }, [empresaActual, showForm, bulkTrabajadorId, bulkFecha]);

  // Helpers de filas. Al agregar una fila nueva, hereda la operación de la
  // última fila para no obligar a la chica a re-seleccionarla cuando carga
  // la misma operación en varios cortes seguidos (caso muy común: "Cintura"
  // en 5 lotes distintos).
  const addFila = () => setFilas(p => {
    const ultima = p[p.length - 1];
    return [...p, {
      ...emptyRow(),
      operacion_id: ultima?.operacion_id || '',
    }];
  });
  const removeFila = (uid) => setFilas(p => p.length === 1 ? [emptyRow()] : p.filter(f => f.uid !== uid));
  const updateFila = (uid, patch) => setFilas(p => p.map(f => f.uid === uid ? { ...f, ...patch } : f));
  const seleccionarCorteEnFila = (uid, corte) => updateFila(uid, {
    registro_id: corte.id,
    cantidad: corte.cantidad_sugerida || '',
    corteListOpen: false,
    searchCorte: '',
  });

  // Cortes filtrados localmente por fila (para que el search de cada fila
  // afecte solo su lista, sin re-fetchear).
  const cortesFiltrados = useCallback((searchInFila) => {
    const q = (searchInFila || '').toLowerCase().trim();
    if (!q) return cortes;
    return cortes.filter(c =>
      (c.n_corte || '').toLowerCase().includes(q) ||
      (c.modelo_nombre || '').toLowerCase().includes(q)
    );
  }, [cortes]);

  // Cálculos por fila
  const operacionMap = useMemo(() => Object.fromEntries(operaciones.map(o => [String(o.id), o])), [operaciones]);
  const importeFila = (fila) => {
    const op = operacionMap[String(fila.operacion_id)];
    if (!op) return 0;
    const tarifa = fila.usaTarifaCustom
      ? (parseFloat(fila.tarifa_aplicada) || 0)
      : Number(op.tarifa_unitaria);
    const cant = parseInt(fila.cantidad) || 0;
    return Math.round(tarifa * cant * 100) / 100;
  };
  const totalBulk = useMemo(
    () => Math.round(filas.reduce((s, f) => s + importeFila(f), 0) * 100) / 100,
    // importeFila es estable porque solo depende de operacionMap (en deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filas, operacionMap]
  );
  const filasValidas = useMemo(
    () => filas.filter(f => f.registro_id && f.operacion_id && parseInt(f.cantidad) > 0).length,
    [filas]
  );

  const startNew = () => {
    setBulkTrabajadorId('');
    setBulkFecha(today());
    setFilas([emptyRow()]);
    setShowForm(true);
  };
  const cancel = () => {
    setShowForm(false);
    setBulkTrabajadorId('');
    setFilas([emptyRow()]);
  };

  const guardarBulk = async (e) => {
    e.preventDefault();
    if (!bulkTrabajadorId) { toast.error('Elegí un costurero'); return; }
    if (!bulkFecha) { toast.error('Falta la fecha'); return; }
    const validas = filas.filter(f => f.registro_id && f.operacion_id && parseInt(f.cantidad) > 0);
    if (!validas.length) {
      toast.error('Agregá al menos una fila completa (modelo + operación + cantidad)');
      return;
    }
    setSaving(true);
    const payload = {
      trabajador_id: parseInt(bulkTrabajadorId),
      fecha: bulkFecha,
      items: validas.map(f => ({
        operacion_id: parseInt(f.operacion_id),
        registro_id: f.registro_id,
        cantidad: parseInt(f.cantidad),
        tarifa_aplicada: f.usaTarifaCustom ? (parseFloat(f.tarifa_aplicada) || 0) : null,
        notas: f.notas?.trim() || null,
      })),
      permitir_duplicados: false,
    };
    try {
      const r = await createCosturaAvancesBulk(payload);
      toast.success(`${r.data.creados} operación(es) registrada(s) — Total ${fmtMoney(r.data.total)}`);
      cancel();
      cargarAvances();
    } catch (e) {
      // Si el backend detecta duplicados (409), preguntar al usuario si igual quiere crearlos
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        const dups = detail.duplicados || [];
        const lista = dups.map(d => `  • ${d.operacion_nombre} en ${d.n_corte} (${d.modelo_nombre}) — ${d.cantidad}u`).join('\n');
        const ok = window.confirm(
          `${detail.mensaje || 'Hay items duplicados.'}\n\nDuplicados:\n${lista}\n\n¿Crearlos igualmente?`
        );
        if (ok) {
          try {
            const r2 = await createCosturaAvancesBulk({ ...payload, permitir_duplicados: true });
            toast.success(`${r2.data.creados} operación(es) registrada(s) — Total ${fmtMoney(r2.data.total)}`);
            cancel();
            cargarAvances();
            return;
          } catch (e2) {
            toast.error(typeof e2.response?.data?.detail === 'string' ? e2.response.data.detail : 'Error al guardar');
          }
        } else {
          toast.message('Carga cancelada — corregí o quita las filas duplicadas');
        }
      } else {
        toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Edición individual ───────────────────────────────────────────────
  const startEdit = (av) => {
    setEditingAvance(av);
    setEditForm({
      cantidad: String(av.cantidad),
      fecha: av.fecha ? av.fecha.split('T')[0] : today(),
      notas: av.notas || '',
      tarifa_aplicada: av.tarifa_aplicada,
      usaTarifaCustom: false,
    });
  };
  const cancelEdit = () => { setEditingAvance(null); setEditForm(null); };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingAvance || !editForm) return;
    if (parseInt(editForm.cantidad) <= 0) { toast.error('Cantidad inválida'); return; }
    setSavingEdit(true);
    try {
      await updateCosturaAvance(editingAvance.id, {
        trabajador_id: editingAvance.trabajador_id,
        operacion_id: editingAvance.operacion_id,
        registro_id: editingAvance.registro_id,
        cantidad: parseInt(editForm.cantidad),
        fecha: editForm.fecha,
        notas: editForm.notas?.trim() || null,
        tarifa_aplicada: editForm.usaTarifaCustom ? (parseFloat(editForm.tarifa_aplicada) || 0) : null,
      });
      toast.success('Operación actualizada');
      cancelEdit();
      cargarAvances();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (av) => {
    if (av.planilla_destajo_id) {
      toast.error('No se puede eliminar: ya está vinculado a una planilla destajo');
      return;
    }
    if (!window.confirm(`¿Eliminar operación de ${av.trabajador_nombre} — ${av.operacion_nombre} (${av.cantidad} u, ${fmtMoney(av.importe)})?`)) return;
    try {
      await deleteCosturaAvance(av.id);
      toast.success('Operación eliminada');
      cargarAvances();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Filtro local libre por modelo / n° corte / costurero / operación.
  // No hace fetch — opera sobre `items` ya cargados desde el backend.
  const itemsFiltrados = useMemo(() => {
    const q = (filtros.busqueda || '').toLowerCase().trim();
    if (!q) return items;
    return items.filter(it => {
      const haystack = [
        it.modelo_nombre, it.n_corte, it.trabajador_nombre, it.operacion_nombre, it.notas,
      ].filter(Boolean).join(' · ').toLowerCase();
      return haystack.includes(q);
    });
  }, [items, filtros.busqueda]);

  const totales = useMemo(() => {
    let cantidad = 0, importe = 0, pendiente = 0;
    itemsFiltrados.forEach(it => {
      cantidad += it.cantidad || 0;
      importe += Number(it.importe || 0);
      if (!it.planilla_destajo_id) pendiente += Number(it.importe || 0);
    });
    return { cantidad, importe, pendiente };
  }, [itemsFiltrados]);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" />
            Operaciones de Costura
          </h1>
          <p className="text-sm text-gray-500">
            Registro de operaciones <strong>terminadas</strong> por los costureros del taller propio.
            Se carga al cierre (un día antes de pagar la planilla destajo). Cada fila es una
            operación completa hecha por un costurero sobre un lote.
          </p>
        </div>
        <button
          onClick={startNew}
          className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 flex items-center gap-1"
          data-testid="btn-nuevo-avance"
        >
          <Layers className="h-4 w-4" /> Nueva carga
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border p-3 bg-blue-50">
          <div className="text-xs uppercase tracking-wider text-blue-700">Operaciones registradas</div>
          <div className="text-xl font-bold mt-1">
            {itemsFiltrados.length}
            {itemsFiltrados.length !== items.length && (
              <span className="text-xs text-gray-500 font-normal ml-1">de {items.length}</span>
            )}
          </div>
        </div>
        <div className="rounded-lg border p-3 bg-gray-50">
          <div className="text-xs uppercase tracking-wider text-gray-700">Total prendas operadas</div>
          <div className="text-xl font-bold font-mono mt-1">{totales.cantidad}</div>
        </div>
        <div className="rounded-lg border p-3 bg-emerald-50">
          <div className="text-xs uppercase tracking-wider text-emerald-700">
            Importe pendiente <span className="opacity-60">/ total</span>
          </div>
          <div className="text-xl font-bold font-mono mt-1">
            {fmtMoney(totales.pendiente)} <span className="text-xs text-gray-500">/ {fmtMoney(totales.importe)}</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-gray-500 block">Buscar</label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={filtros.busqueda}
              onChange={(e) => setFiltros(p => ({ ...p, busqueda: e.target.value }))}
              placeholder="modelo, n° corte, costurero, operación…"
              className="border rounded pl-7 pr-7 py-1 text-sm h-8 w-full"
              data-testid="input-busqueda"
            />
            {filtros.busqueda && (
              <button
                type="button"
                onClick={() => setFiltros(p => ({ ...p, busqueda: '' }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Costurero</label>
          <select value={filtros.trabajador_id} onChange={(e) => setFiltros(p => ({ ...p, trabajador_id: e.target.value }))} className="border rounded px-2 py-1 text-sm h-8">
            <option value="">Todos</option>
            {trabajadores.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Operación</label>
          <select value={filtros.operacion_id} onChange={(e) => setFiltros(p => ({ ...p, operacion_id: e.target.value }))} className="border rounded px-2 py-1 text-sm h-8">
            <option value="">Todas</option>
            {operaciones.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Desde</label>
          <input type="date" value={filtros.fecha_desde} onChange={(e) => setFiltros(p => ({ ...p, fecha_desde: e.target.value }))} className="border rounded px-2 py-1 text-sm h-8" />
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Hasta</label>
          <input type="date" value={filtros.fecha_hasta} onChange={(e) => setFiltros(p => ({ ...p, fecha_hasta: e.target.value }))} className="border rounded px-2 py-1 text-sm h-8" />
        </div>
        <label className="text-xs flex items-center gap-1 cursor-pointer pb-1">
          <input type="checkbox" checked={filtros.solo_pendientes} onChange={(e) => setFiltros(p => ({ ...p, solo_pendientes: e.target.checked }))} />
          Solo pendientes de pago
        </label>
      </div>

      {/* === FORM DE CARGA MASIVA === */}
      {showForm && (
        <form onSubmit={guardarBulk} className="rounded-lg border bg-white p-4 space-y-3" data-testid="form-bulk">
          <h2 className="text-sm font-semibold flex items-center gap-1">
            <Plus className="h-4 w-4" /> Nueva carga de operaciones
          </h2>

          {/* Cabecera: costurero + fecha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pb-2 border-b">
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Costurero <span className="text-red-500">*</span></label>
              <select
                value={bulkTrabajadorId}
                onChange={(e) => setBulkTrabajadorId(e.target.value)}
                className="border rounded px-2 py-1.5 text-sm w-full"
                required
                data-testid="bulk-select-trabajador"
              >
                <option value="">Seleccionar…</option>
                {trabajadores.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Fecha <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={bulkFecha}
                onChange={(e) => setBulkFecha(e.target.value)}
                className="border rounded px-2 py-1.5 text-sm w-full"
                required
              />
            </div>
            <label className="flex items-end gap-1 text-xs cursor-pointer pb-1.5">
              <input type="checkbox" checked={soloInterna} onChange={(e) => setSoloInterna(e.target.checked)} />
              Solo costura interna (cortes que pasan por taller propio)
            </label>
          </div>

          {/* Tabla de filas */}
          {!bulkTrabajadorId ? (
            <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed rounded">
              Elegí primero el costurero arriba para cargar las operaciones
            </div>
          ) : (
            <>
              {existentes.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs flex items-start gap-2 mb-2">
                  <span className="text-amber-600 mt-0.5">⚠</span>
                  <div>
                    Ya hay <strong>{existentes.length}</strong> operación(es) cargada(s) para este costurero en {fmtDate(bulkFecha)}.
                    Las filas que repitan exactamente la misma combinación (operación + corte) se marcan en ámbar — el sistema te avisará al guardar para que confirmes si es intencional.
                  </div>
                </div>
              )}
              <div className="rounded-lg border overflow-visible">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                    <tr>
                      <th className="text-left px-2 py-1.5 w-8">#</th>
                      <th className="text-left px-2 py-1.5">Modelo / Corte *</th>
                      <th className="text-left px-2 py-1.5 w-44">Operación *</th>
                      <th className="text-right px-2 py-1.5 w-24">Cantidad *</th>
                      <th className="text-right px-2 py-1.5 w-28">Tarifa</th>
                      <th className="text-right px-2 py-1.5 w-28">Importe</th>
                      <th className="text-left px-2 py-1.5 w-44">Notas</th>
                      <th className="text-center px-2 py-1.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((fila, idx) => {
                      const op = operacionMap[String(fila.operacion_id)];
                      const corteSel = cortes.find(c => String(c.id) === String(fila.registro_id));
                      const cortesView = cortesFiltrados(fila.searchCorte);
                      // Detección proactiva de duplicado para esta fila.
                      const esDuplicado = !!(fila.operacion_id && fila.registro_id
                        && dupSet.has(dupKey(parseInt(fila.operacion_id), fila.registro_id)));
                      return (
                        <tr key={fila.uid} className={`border-t align-top ${esDuplicado ? 'bg-amber-50' : ''}`}>
                          <td className="px-2 py-1.5 text-xs text-gray-400 pt-3">
                            {idx + 1}
                            {esDuplicado && (
                              <div title="Ya existe una operación con esta combinación para esta fecha">
                                <span className="block mt-1 text-amber-600 text-[10px]">⚠</span>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 relative">
                            {corteSel ? (
                              <div className="flex items-center gap-1.5 px-2 py-1 border rounded bg-emerald-50 border-emerald-200">
                                <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{corteSel.modelo_nombre || '(sin modelo)'}</div>
                                  <div className="text-[10px] text-gray-500 font-mono">{corteSel.n_corte} · {corteSel.estado}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateFila(fila.uid, { registro_id: '', cantidad: '', corteListOpen: true })}
                                  className="text-[10px] text-gray-500 hover:text-gray-900 px-1.5 py-0.5 rounded hover:bg-white"
                                >
                                  Cambiar
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="relative">
                                  <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                                  <input
                                    type="text"
                                    value={fila.searchCorte}
                                    onChange={(e) => updateFila(fila.uid, { searchCorte: e.target.value, corteListOpen: true })}
                                    onFocus={() => updateFila(fila.uid, { corteListOpen: true })}
                                    placeholder="Buscar modelo o n° corte"
                                    className="border rounded pl-6 pr-2 py-1 text-xs w-full"
                                  />
                                </div>
                                {fila.corteListOpen && (
                                  <div className="absolute left-2 right-2 z-20 mt-1 border rounded bg-white max-h-48 overflow-y-auto shadow-lg">
                                    {loadingCortes ? (
                                      <div className="px-2 py-1.5 text-[11px] text-gray-400">Cargando…</div>
                                    ) : cortesView.length === 0 ? (
                                      <div className="px-2 py-1.5 text-[11px] text-gray-400">Sin resultados</div>
                                    ) : cortesView.slice(0, 30).map(c => (
                                      <button
                                        type="button"
                                        key={c.id}
                                        onClick={() => seleccionarCorteEnFila(fila.uid, c)}
                                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 border-b last:border-0"
                                      >
                                        <div className="font-medium">{c.modelo_nombre || '(sin modelo)'}</div>
                                        <div className="text-[10px] text-gray-500 font-mono flex items-center justify-between">
                                          <span>{c.n_corte} · {c.estado}</span>
                                          {c.cantidad_sugerida > 0 && <span className="text-emerald-600 font-semibold">{c.cantidad_sugerida} u</span>}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={fila.operacion_id}
                              onChange={(e) => updateFila(fila.uid, { operacion_id: e.target.value })}
                              className="border rounded px-1 py-1 text-xs w-full"
                            >
                              <option value="">Seleccionar…</option>
                              {operaciones.map(o => (
                                <option key={o.id} value={o.id}>{o.nombre} ({fmtMoney(o.tarifa_unitaria)})</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="1"
                              value={fila.cantidad}
                              onChange={(e) => updateFila(fila.uid, { cantidad: e.target.value })}
                              className="border rounded px-1 py-1 text-xs w-full text-right font-mono"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            {op ? (
                              <div className="text-xs text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="checkbox"
                                    checked={fila.usaTarifaCustom}
                                    onChange={(e) => updateFila(fila.uid, { usaTarifaCustom: e.target.checked })}
                                    title="Usar tarifa especial"
                                    className="h-3 w-3"
                                  />
                                  {fila.usaTarifaCustom ? (
                                    <input
                                      type="number" step="0.0001" min="0"
                                      value={fila.tarifa_aplicada}
                                      onChange={(e) => updateFila(fila.uid, { tarifa_aplicada: e.target.value })}
                                      className="border rounded px-1 py-0.5 w-16 text-right font-mono"
                                      placeholder={op.tarifa_unitaria}
                                    />
                                  ) : (
                                    <span className="font-mono">{fmtMoney(op.tarifa_unitaria)}</span>
                                  )}
                                </div>
                              </div>
                            ) : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-700 text-sm">
                            {fmtMoney(importeFila(fila))}
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={fila.notas}
                              onChange={(e) => updateFila(fila.uid, { notas: e.target.value })}
                              placeholder="(opcional)"
                              className="border rounded px-1 py-1 text-xs w-full"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() => removeFila(fila.uid)}
                              className="text-red-500 hover:bg-red-50 rounded p-0.5"
                              title="Quitar fila"
                            >
                              <XIcon className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t">
                      <td colSpan={5} className="px-2 py-2">
                        <button
                          type="button"
                          onClick={addFila}
                          className="text-xs text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1"
                          data-testid="btn-add-fila"
                        >
                          <Plus className="h-3.5 w-3.5" /> Agregar otra fila
                        </button>
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-base text-emerald-700">
                        {fmtMoney(totalBulk)}
                      </td>
                      <td colSpan={2} className="px-2 py-2 text-xs text-gray-500">
                        {filasValidas} de {filas.length} válidas
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-gray-500">
                Tip: al elegir el modelo, se rellena la cantidad sugerida (= cantidad del movimiento de costura del costurero o del lote completo). Editala si difiere de lo que reportó el operario.
              </p>
            </>
          )}

          <div className="flex gap-2 justify-end pt-2 border-t">
            <button type="button" onClick={cancel} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50" disabled={saving}>
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50"
              disabled={saving || filasValidas === 0}
              data-testid="btn-guardar-bulk"
            >
              <Check className="h-4 w-4" /> {saving ? 'Guardando…' : `Guardar ${filasValidas} operación(es)`}
            </button>
          </div>
        </form>
      )}

      {/* === MODAL DE EDICIÓN INDIVIDUAL === */}
      {editingAvance && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={cancelEdit}>
          <form
            onSubmit={saveEdit}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold flex items-center gap-1">
              <Edit2 className="h-4 w-4" /> Editar operación
            </h2>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 space-y-0.5">
              <div><strong>Costurero:</strong> {editingAvance.trabajador_nombre}</div>
              <div><strong>Operación:</strong> {editingAvance.operacion_nombre}</div>
              <div><strong>N° corte:</strong> {editingAvance.n_corte}{editingAvance.modelo_nombre ? ` — ${editingAvance.modelo_nombre}` : ''}</div>
              <div className="text-[10px] text-amber-700 mt-1">El costurero, operación y corte no se editan — si necesitás cambiarlos, eliminá y registrá uno nuevo.</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 block">Cantidad *</label>
                <input
                  type="number" min="1"
                  value={editForm.cantidad}
                  onChange={(e) => setEditForm(p => ({ ...p, cantidad: e.target.value }))}
                  className="border rounded px-2 py-1 text-sm w-full font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block">Fecha</label>
                <input
                  type="date"
                  value={editForm.fecha}
                  onChange={(e) => setEditForm(p => ({ ...p, fecha: e.target.value }))}
                  className="border rounded px-2 py-1 text-sm w-full"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 block">Notas</label>
              <input
                type="text"
                value={editForm.notas}
                onChange={(e) => setEditForm(p => ({ ...p, notas: e.target.value }))}
                className="border rounded px-2 py-1 text-sm w-full"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <button type="button" onClick={cancelEdit} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50" disabled={savingEdit}>
                Cancelar
              </button>
              <button type="submit" className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700" disabled={savingEdit}>
                {savingEdit ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* === LISTA DE AVANCES === */}
      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Costurero</th>
              <th className="text-left px-3 py-2">Operación</th>
              <th className="text-left px-3 py-2">N° Corte / Modelo</th>
              <th className="text-right px-3 py-2">Cantidad</th>
              <th className="text-right px-3 py-2">Tarifa</th>
              <th className="text-right px-3 py-2">Importe</th>
              <th className="text-center px-3 py-2 w-28">Estado</th>
              <th className="text-center px-3 py-2 w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">Cargando…</td></tr>
            ) : itemsFiltrados.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">
                {filtros.busqueda ? 'Sin resultados para la búsqueda' : 'Sin operaciones registradas'}
              </td></tr>
            ) : itemsFiltrados.map(av => (
              <tr key={av.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-xs">{fmtDate(av.fecha)}</td>
                <td className="px-3 py-2 font-medium">{av.trabajador_nombre || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-700">{av.operacion_nombre || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-mono">{av.n_corte || '—'}</div>
                  {av.modelo_nombre && (
                    <div className="text-[11px] text-gray-500 font-normal mt-0.5">
                      {av.modelo_nombre}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{av.cantidad}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(av.tarifa_aplicada)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(av.importe)}</td>
                <td className="px-3 py-2 text-center">
                  {av.planilla_destajo_id ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Pagado #{av.planilla_destajo_id}</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pendiente</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => startEdit(av)} className="p-1 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30" title="Editar" disabled={!!av.planilla_destajo_id} data-testid={`btn-edit-${av.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(av)} className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-30" title="Eliminar" disabled={!!av.planilla_destajo_id} data-testid={`btn-del-${av.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
